import { useEffect, useRef } from 'react'
import { analytics } from '../utils/analytics'

const POLL_INTERVAL_MS = 60_000
const VERSION_URL = '/version.json'

type Manifest = { sha: string; builtAt?: string }

export type NewVersionEventDetail = {
    currentSha: string
    latestSha: string
    detectedAt: number
}

const BUNDLED_SHA: string = (import.meta.env.VITE_BUILD_SHA as string | undefined) ?? 'dev'

async function fetchManifest(): Promise<Manifest | null> {
    try {
        const res = await fetch(VERSION_URL, { cache: 'no-store' })
        if (!res.ok) return null
        return (await res.json()) as Manifest
    } catch {
        return null
    }
}

export function useVersionCheck(): void {
    const announcedRef = useRef(false)

    useEffect(() => {
        // Skip in dev — Vite HMR handles reloads.
        if (import.meta.env.DEV) return
        // No bundled SHA = local/preview build without git; nothing to compare.
        if (BUNDLED_SHA === 'dev') return

        const check = async () => {
            if (announcedRef.current) return
            const manifest = await fetchManifest()
            if (!manifest?.sha) return
            if (manifest.sha === BUNDLED_SHA) return

            announcedRef.current = true
            const detail: NewVersionEventDetail = {
                currentSha: BUNDLED_SHA,
                latestSha: manifest.sha,
                detectedAt: Date.now(),
            }
            analytics.versionPromptShown(detail.currentSha, detail.latestSha)
            window.dispatchEvent(new CustomEvent('newVersionAvailable', { detail }))
        }

        void check()
        const id = window.setInterval(() => void check(), POLL_INTERVAL_MS)
        const onVisible = () => {
            if (document.visibilityState === 'visible') void check()
        }
        document.addEventListener('visibilitychange', onVisible)

        return () => {
            window.clearInterval(id)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [])
}
